import classNames from 'classnames';
import {ColumnCellProps} from 'fixed-data-table-2';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import * as keyboard from 'util/Keyboard';
import {BasicEvent, ElementMouseEvent} from 'util/Types';

import isRowIndexOutOfBounds from './isRowIndexOutOfBounds';
import {ScrollableTableRow, RowSelectionMode} from './ScrollableTableConstants';

interface ScrollableTableCellProps extends Pick<ColumnCellProps, 'rowIndex'> {
  accessorKey: string;
  collapsedColumns?: Array<string>; // Placeholder type, until collapsed column support is added
  isSelectorPresent: boolean;
  isSelectorDisabled: boolean;
  isFixed: boolean;
  isPrimary: boolean;
  onContextMenu?: Function;
  onSelect?: (row: ScrollableTableRow, isSelected: boolean, isShift: boolean) => void;
  rows: Array<ScrollableTableRow>;
  numLinesToWrapText?: number;
  dataTestAttrs?: DataTestAttrs;
  alwaysShowSelector?: boolean;
  rowSelectionMode?: RowSelectionMode | null;
  /** If provided, a corresponding BenchlingIcon will be used for the selector rather than the default radio
   * button or checkbox input. The same handleSelect functionality will be used. */
  selectorIconProps?: {icon: BenchlingIconName; iconStyle: ClickableIconStyle};
}

export default class ScrollableTableCell extends React.PureComponent<ScrollableTableCellProps> {
  private getRow(): ScrollableTableRow {
    return this.props.rows[this.props.rowIndex];
  }

  private handleSelect = (e: BasicEvent): void => {
    if (this.props.onSelect) {
      this.props.onSelect(
        this.getRow(),
        !this.getRow().isSelected,
        keyboard.isShiftClick(e as ElementMouseEvent)
      );
    }
  };

  // When clicking the checkbox or label, need to stop propagation, otherwise the TR's onClick will
  // fire
  private handleCheckboxClick = (e: ElementMouseEvent): void => {
    e.stopPropagation();
  };

  private handleContextMenu = (e: BasicEvent): void => {
    if (this.props.onContextMenu) {
      this.props.onContextMenu(this.props.rows[this.props.rowIndex], e);
    }
  };

  private renderIcon(row: ScrollableTableRow): JSX.Element {
    return (
      <div>
        {this.props.isSelectorPresent && (
          <label
            className={classNames('scrollableTable-cell-iconWrapper', {
              'tableRow--visibleOnHover-content':
                row.icon != null &&
                (row.isSelected == null || row.isSelected === false) &&
                !this.props.alwaysShowSelector,
            })}
            onClick={this.handleCheckboxClick}
            {...getTestProps({component: 'ScrollableTableCell', element: 'checkboxWrapper'})}
          >
            {this.props.selectorIconProps != null ? (
              <ClickableIcon
                icon={this.props.selectorIconProps.icon}
                onClick={this.handleSelect}
                disabled={this.props.isSelectorDisabled}
                iconStyle={this.props.selectorIconProps.iconStyle}
              />
            ) : (
              <input
                checked={(row.isSelected || row.displayAsSelected) ?? false}
                className={
                  this.props.rowSelectionMode === RowSelectionMode.SINGLE
                    ? 'scrollableTable-cell-radio'
                    : 'scrollableTable-cell-checkbox'
                }
                onChange={this.handleSelect}
                onClick={this.handleCheckboxClick}
                type={this.props.rowSelectionMode === RowSelectionMode.SINGLE ? 'radio' : 'checkbox'}
                disabled={this.props.isSelectorDisabled}
              />
            )}
          </label>
        )}
        {row.icon && !row.isSelected && !this.props.alwaysShowSelector && (
          <div
            className={classNames('scrollableTable-cell-iconWrapper', {
              'tableRow--hiddenOnHover-content': this.props.isSelectorPresent,
            })}
            {...getTestProps({component: 'ScrollableTableCell', element: 'iconWrapper'})}
          >
            {row.icon}
          </div>
        )}
      </div>
    );
  }

  override render(): JSX.Element | null {
    if (isRowIndexOutOfBounds(this.props.rows, this.props.rowIndex)) {
      return null;
    }
    const row = this.getRow();
    const cellClass = classNames(
      'scrollableTable-cell u-vertical-center-items',
      row.classNames ? row.classNames[this.props.accessorKey] : '',
      {'scrollableTable-cell--isPrimary': this.props.isPrimary}
    );
    return (
      <Tooltip
        placement={FloatingPlacement.TOP}
        text={row.tooltipText}
        isDisabled={row.tooltipText == null}
        disableContentSelection={true}
      >
        <div
          className={cellClass}
          onContextMenu={this.handleContextMenu}
          {...getTestProps(this.props.dataTestAttrs)}
        >
          {(() => {
            if (
              this.props.collapsedColumns != null &&
              this.props.collapsedColumns.indexOf(this.props.accessorKey) >= 0
            ) {
              return null;
            }
            return (
              <div
                className={classNames({'u-position-relative u-flex-grow': !this.props.isFixed})}
                {...row.eventHandlers}
              >
                {this.props.isPrimary && this.renderIcon(row)}
                <span
                  className={classNames(
                    {
                      'scrollableTableRow-iconCellText':
                        this.props.isPrimary && (row.icon != null || this.props.isSelectorPresent),
                    },
                    {'scrollableTable-cell--wrapText': Boolean(this.props.numLinesToWrapText)}
                  )}
                  style={
                    this.props.numLinesToWrapText
                      ? {WebkitLineClamp: this.props.numLinesToWrapText}
                      : undefined
                  }
                  {...getTestProps({component: 'ScrollableTableCell', element: 'text'})}
                >
                  {row.data[this.props.accessorKey]}
                </span>
              </div>
            );
          })()}
        </div>
      </Tooltip>
    );
  }
}
